"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, RotateCcw, Home, Download, ChevronDown, ChevronUp } from "lucide-react";
import { useFilesStore } from "@/stores/files-store";

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function WorkspaceErrorPage({ error, reset }: ErrorPageProps) {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Log the error to an analytics or error tracking service
    console.error("Workspace render error captured by boundary:", error);
  }, [error]);

  const handleBackup = () => {
    try {
      const files = useFilesStore.getState().files;
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(files, null, 2));
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `wiki_arsenal_respaldo_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    } catch (err) {
      alert("No se pudo realizar el respaldo de las notas locales.");
    }
  };

  const handleGoHome = () => {
    window.location.href = "/workspace";
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black text-slate-100 selection:bg-primary/30">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl p-8 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-2xl relative overflow-hidden"
      >
        {/* Decorative ambient light */}
        <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-rose-500/10 blur-3xl" />

        <div className="flex flex-col items-center text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/25 flex items-center justify-center text-rose-500 mb-6 shadow-lg shadow-rose-500/5">
            <AlertCircle className="w-8 h-8" strokeWidth={1.5} />
          </div>

          <h1 className="text-xl font-bold text-white mb-2 tracking-tight">
            Algo no salió como se esperaba
          </h1>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed mb-8">
            El espacio de trabajo experimentó un error inesperado. Puedes intentar recuperar la sesión o descargar un respaldo de tus notas antes de continuar.
          </p>

          {/* Action buttons */}
          <div className="w-full flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 h-10 px-5 bg-primary text-primary-foreground font-semibold rounded-xl text-xs hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md shadow-primary/25"
            >
              <RotateCcw className="w-4 h-4" />
              Reintentar carga
            </button>

            <button
              onClick={handleBackup}
              className="flex items-center justify-center gap-2 h-10 px-5 bg-white/5 border border-white/10 text-white font-semibold rounded-xl text-xs hover:bg-white/10 active:scale-[0.98] transition-all"
              title="Descargar notas locales en formato JSON"
            >
              <Download className="w-4 h-4" />
              Respaldar notas (JSON)
            </button>

            <button
              onClick={handleGoHome}
              className="flex items-center justify-center gap-2 h-10 px-5 bg-white/5 border border-white/10 text-white font-semibold rounded-xl text-xs hover:bg-white/10 active:scale-[0.98] transition-all"
            >
              <Home className="w-4 h-4" />
              Ir al Inicio
            </button>
          </div>

          {/* Error Details Accordion */}
          <div className="w-full border-t border-white/10 pt-4 text-left">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors py-1"
            >
              <span>Detalles técnicos del error</span>
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-3 p-4 rounded-xl bg-black/40 border border-white/5 font-mono text-[11px] text-slate-300 overflow-x-auto max-h-[160px] custom-scrollbar"
              >
                <div className="font-bold text-rose-400 mb-1">{error.name || "Error"}:</div>
                <div className="whitespace-pre-wrap">{error.message || "No hay un mensaje de error disponible."}</div>
                {error.stack && (
                  <div className="mt-2 text-slate-500 text-[10px] whitespace-pre-wrap border-t border-white/5 pt-2">
                    {error.stack}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
